import { PortraitGrid, PortraitDialogButton } from "@/components/PortraitPicker";
import VoicesLibrary from "@/components/caregiver/VoicesLibrary";
import TeamMessagesCard from "@/components/caregiver/TeamMessagesCard";
import WorkshopPhotosCard from "@/components/caregiver/WorkshopPhotosCard";
import FavoriteSongsPicker from "@/components/music/FavoriteSongsPicker";
import { GardiensGallery } from "@/pages/Gardiens";
import { useState, useEffect, useCallback } from "react";
import { GardienBadge } from "@/components/GardienBadge";
import { GardienPickerButton, GardienCardButton, GardienActivities } from "@/components/GardienPicker";
import NewVoices from "@/components/caregiver/NewVoices";
import FamilyInvites from "@/components/caregiver/FamilyInvites";
import { Switch } from "@/components/ui/switch";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { TrialBanner, trialDaysLeft } from "@/components/TrialBanner";
import BookletButton from "@/components/BookletButton";
import { useAuth } from "@/context/AuthContext";
import { AVATARS, LOGO } from "@/data/exercises";
import { GroupsManager } from "@/components/caregiver/GroupsManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  LogOut, Plus, Play, Trash2, Brain, Hand, Clock, Activity, Users, ArrowLeft, UserPlus, Shield, CalendarDays, LayoutGrid, Mail, UserPlus2, Trash, CreditCard, FileText, Feather } from "lucide-react";

const CONDITIONS = [
  { value: "alzheimer", label: "Alzheimer" },
  { value: "autisme", label: "Autisme" },
  { value: "degenerative", label: "Maladie dégénérative" },
  { value: "autre", label: "Autre" },
];

function StatCard({ icon: Icon, label, value, tint }) {
  return (
    <Card className="border-gray-200">
      <CardContent className="p-5 flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: tint }}>
          <Icon className="w-6 h-6 text-emerald-800" />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-800">{value}</p>
          <p className="text-sm text-gray-500">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function AddPatientDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [condition, setCondition] = useState("alzheimer");
  const [notes, setNotes] = useState("");
  const [avatar, setAvatar] = useState(AVATARS[0]);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) return toast.error("Veuillez indiquer un nom.");
    setSaving(true);
    try {
      await api.post("/patients", { name, condition, notes, avatar });
      toast.success("Profil créé");
      setOpen(false);
      setName(""); setNotes(""); setCondition("alzheimer");
      onCreated();
    } catch {
      toast.error("Impossible de créer le profil.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-emerald-700 hover:bg-emerald-800 rounded-xl" data-testid="add-patient-button">
          <Plus className="w-4 h-4 mr-1" /> Nouveau patient
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="add-patient-dialog">
        <DialogHeader>
          <DialogTitle>Nouveau profil patient</DialogTitle>
          <DialogDescription>Renseignez les informations du patient à accompagner.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Nom</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Prénom du patient"
              className="mt-1" data-testid="patient-name-input" />
          </div>
          <div>
            <Label>Situation</Label>
            <Select value={condition} onValueChange={setCondition}>
              <SelectTrigger className="mt-1" data-testid="patient-condition-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONDITIONS.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Portrait</Label>
            <div className="mt-2"><PortraitGrid value={avatar} onChange={setAvatar} compact /></div>
          </div>
          <div>
            <Label>Notes (facultatif)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Observations, préférences..." className="mt-1" data-testid="patient-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800"
            data-testid="save-patient-button">
            {saving ? "Enregistrement..." : "Créer le profil"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PatientDetail({ patient, onBack }) {
  const [gardien, setGardien] = useState(patient.gardien_id || "");
  const [portrait, setPortrait] = useState(patient.avatar || AVATARS[0]);
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [weekly, setWeekly] = useState([]);
  const [notes, setNotes] = useState([]);
  const [family, setFamily] = useState([]);
  const [famForm, setFamForm] = useState({ name: "", email: "", password: "" });
  const [famOpen, setFamOpen] = useState(false);
  const [famSaving, setFamSaving] = useState(false);

  const loadFamily = () => api.get(`/patients/${patient.id}/family`).then((r) => setFamily(r.data));

  useEffect(() => {
    api.get(`/stats/${patient.id}`).then((r) => setStats(r.data));
    api.get(`/sessions/${patient.id}`).then((r) => setSessions(r.data));
    api.get(`/stats/${patient.id}/weekly`).then((r) => setWeekly(r.data));
    api.get(`/patients/${patient.id}/notes`).then((r) => setNotes(r.data));
    loadFamily();
  }, [patient.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const createFamily = async () => {
    if (!famForm.name.trim() || !famForm.email.trim() || famForm.password.length < 6)
      return toast.error("Nom, e-mail et mot de passe (6+ caractères) requis.");
    setFamSaving(true);
    try {
      await api.post(`/patients/${patient.id}/family`, famForm);
      toast.success("Accès famille créé");
      setFamOpen(false);
      setFamForm({ name: "", email: "", password: "" });
      loadFamily();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Création impossible.");
    } finally {
      setFamSaving(false);
    }
  };

  const removeFamily = async (id) => {
    try {
      await api.delete(`/family-accounts/${id}`);
      loadFamily();
    } catch {
      toast.error("Suppression impossible.");
    }
  };

  const shareNote = async (n, shared) => {
    try {
      await api.put(`/patients/${patient.id}/notes/${n.id}/share`, { shared });
      setNotes((l) => l.map((x) => (x.id === n.id ? { ...x, shared_with_family: shared } : x)));
      toast.success(shared ? "Note partagée avec la famille" : "Note retirée de l'espace famille");
    } catch { toast.error("Modification impossible."); }
  };

  const MOOD_LABELS = { joyeux: "Joyeux", calme: "Calme", fatigue: "Fatigué", agite: "Agité", triste: "Triste" };

  const downloadReport = async () => {
    try {
      const res = await api.get(`/patients/${patient.id}/report/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `suivi-${patient.name}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Suivi PDF téléchargé");
    } catch { toast.error("Téléchargement impossible."); }
  };

  const chartData =
    stats?.timeline.map((t, i) => ({
      name: `S${i + 1}`,
      "Réussite": t.score,
      "Précision": t.accuracy,
    })) || [];

  return (
    <div className="soft-in" data-testid="patient-detail">
      <button onClick={onBack} className="flex items-center gap-2 text-emerald-700 font-semibold mb-6"
        data-testid="detail-back-button">
        <ArrowLeft className="w-4 h-4" /> Retour aux patients
      </button>

      <div className="flex flex-wrap items-center gap-4 mb-8">
        <PortraitDialogButton personName={patient.name} current={portrait}
          onSave={async (a) => { await api.put(`/patients/${patient.id}/avatar`, { avatar: a }); setPortrait(a); }}>
          <img src={portrait} alt={patient.name} className="w-16 h-16 rounded-full object-cover object-top border-4 border-amber-200" data-testid="detail-portrait-image" />
        </PortraitDialogButton>
        <div className="flex-1">
          <h2 className="font-serif-display text-2xl font-normal text-gray-800">{patient.name}</h2>
          <p className="text-gray-500 capitalize">{patient.condition || "—"}</p>
        </div>
        <GardienPickerButton personName={patient.name} current={gardien} testId="detail-gardien-button"
          onSave={async (gid) => { await api.put(`/patients/${patient.id}/gardien`, { gardien_id: gid }); setGardien(gid); }} />
        {gardien && <GardienCardButton url={`/patients/${patient.id}/gardien-card/pdf`} fileName={`carte-gardien-${patient.name}.pdf`} testId="detail-gardien-card" />}
        <BookletButton patient={patient} testId="detail-booklet-button" />
        <Button variant="outline" className="rounded-xl" onClick={downloadReport} data-testid="download-patient-report">
          <FileText className="w-4 h-4 mr-1" /> Suivi PDF
        </Button>
      </div>

      <NewVoices patientId={patient.id} />
      {gardien && <div className="mb-8"><GardienActivities id={gardien} personName={patient.name} /></div>}

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Activity} label="Sessions réalisées" value={stats.total_sessions} tint="#D1FAE5" />
          <StatCard icon={Brain} label="Réussite moyenne" value={`${stats.avg_score}%`} tint="#F8E4C7" />
          <StatCard icon={Hand} label="Précision moyenne" value={`${stats.avg_accuracy}%`} tint="#D1FAE5" />
          <StatCard icon={Clock} label="Temps total" value={`${stats.total_minutes} min`} tint="#F8E4C7" />
        </div>
      )}

      <Card className="border-gray-200 mb-8">
        <CardContent className="p-6">
          <h3 className="font-semibold text-gray-800 mb-4">Progression dans le temps</h3>
          {chartData.length === 0 ? (
            <p className="text-gray-400 py-12 text-center">Aucune session enregistrée pour le moment.</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="name" stroke="#9ca3af" />
                <YAxis domain={[0, 100]} stroke="#9ca3af" />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="Réussite" stroke="#23739F" strokeWidth={3} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="Précision" stroke="#d97706" strokeWidth={3} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card className="border-emerald-200 bg-emerald-50/40 mb-8" data-testid="family-weekly-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-1">
            <CalendarDays className="w-5 h-5 text-emerald-700" />
            <h3 className="font-semibold text-gray-800">Suivi familial — évolution par semaine</h3>
          </div>
          <p className="text-sm text-gray-500 mb-4">Réussite moyenne et nombre de parties, semaine après semaine.</p>
          {weekly.length === 0 ? (
            <p className="text-gray-400 py-10 text-center">Les résultats s'afficheront ici après quelques parties.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={weekly} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" stroke="#6b7280" />
                <YAxis yAxisId="left" domain={[0, 100]} stroke="#6b7280" />
                <YAxis yAxisId="right" orientation="right" allowDecimals={false} stroke="#d97706" />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="avg_score" name="Réussite moyenne (%)" fill="#23739F" radius={[6, 6, 0, 0]} />
                <Bar yAxisId="right" dataKey="sessions" name="Parties jouées" fill="#FBBF24" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card className="border-gray-200">
        <CardContent className="p-6">
          <h3 className="font-semibold text-gray-800 mb-4">Historique des sessions</h3>
          {sessions.length === 0 ? (
            <p className="text-gray-400 py-8 text-center">Pas encore d'activité.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Exercice</TableHead>
                  <TableHead>Catégorie</TableHead>
                  <TableHead className="text-right">Réussite</TableHead>
                  <TableHead className="text-right">Précision</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sessions.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>{new Date(s.completed_at).toLocaleDateString("fr-FR")}</TableCell>
                    <TableCell className="font-medium">{s.exercise_name}</TableCell>
                    <TableCell>{s.category === "cognition" ? "Réflexion" : "Mobilité fine"}</TableCell>
                    <TableCell className="text-right">{s.score}%</TableCell>
                    <TableCell className="text-right">{s.accuracy}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card className="border-gray-200 mt-8">
        <CardContent className="p-6">
          <h3 className="font-semibold text-gray-800 mb-1">Notes de suivi</h3>
          <p className="text-sm text-gray-500 mb-4">Observations de l'équipe soignante après les ateliers.</p>
          {notes.length === 0 ? (
            <p className="text-gray-400 py-6 text-center">Aucune note pour le moment.</p>
          ) : (
            <div className="space-y-3" data-testid="notes-list">
              {notes.map((n) => (
                <div key={n.id} className="rounded-xl border border-gray-200 p-4" data-testid={`note-${n.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-800">{n.exercise_name || "Atelier"}</span>
                    <span className="text-xs text-gray-400">{n.created_at?.slice(0, 10)}</span>
                  </div>
                  <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                    <span>Participation : <b>{n.participation}/5</b></span>
                    <span>Humeur : <b>{MOOD_LABELS[n.mood] || n.mood || "—"}</b></span>
                  </div>
                  {n.observation && <p className="text-gray-600 mt-2 text-sm">{n.observation}</p>}
                  <label className="flex items-center gap-2 mt-3 text-sm text-gray-600 cursor-pointer w-fit">
                    <Switch checked={!!n.shared_with_family} onCheckedChange={(v) => shareNote(n, v)} data-testid={`note-share-${n.id}`} />
                    {n.shared_with_family ? "Visible par la famille" : "Partager avec la famille"}
                  </label>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <TeamMessagesCard patient={patient} />
      <WorkshopPhotosCard patient={patient} />
      <div className="mt-8"><FavoriteSongsPicker getUrl={`/patients/${patient.id}/favorite-tracks`} putUrl={`/patients/${patient.id}/favorite-tracks`} name={patient.name} /></div>
      <VoicesLibrary patient={patient} />

      <Card className="border-gray-200 mt-8">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-semibold text-gray-800">Accès famille</h3>
            <Dialog open={famOpen} onOpenChange={setFamOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 rounded-lg" data-testid="add-family-button">
                  <UserPlus2 className="w-4 h-4 mr-1" /> Créer un accès
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="family-dialog">
                <DialogHeader>
                  <DialogTitle>Accès privé pour la famille</DialogTitle>
                  <DialogDescription>La famille pourra suivre uniquement {patient.name}.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div>
                    <Label>Nom de la famille</Label>
                    <Input value={famForm.name} onChange={(e) => setFamForm({ ...famForm, name: e.target.value })}
                      placeholder="Ex : Famille Martin" className="mt-1" data-testid="family-name-input" />
                  </div>
                  <div>
                    <Label>E-mail (identifiant)</Label>
                    <Input type="email" value={famForm.email} onChange={(e) => setFamForm({ ...famForm, email: e.target.value })}
                      placeholder="famille@exemple.fr" className="mt-1" data-testid="family-email-input" />
                  </div>
                  <div>
                    <Label>Mot de passe</Label>
                    <Input type="text" value={famForm.password} onChange={(e) => setFamForm({ ...famForm, password: e.target.value })}
                      placeholder="Au moins 6 caractères" className="mt-1" data-testid="family-password-input" />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={createFamily} disabled={famSaving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-family-button">
                    {famSaving ? "Création..." : "Créer l'accès"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <p className="text-sm text-gray-500 mb-4">Comptes privés donnant accès aux nouvelles de {patient.name}.</p>
          <FamilyInvites patientId={patient.id} patientName={patient.name} />
          {family.length === 0 ? (
            <p className="text-gray-400 py-6 text-center">Aucun accès famille créé.</p>
          ) : (
            <div className="space-y-2" data-testid="family-list">
              {family.map((f) => (
                <div key={f.id} className="flex items-center justify-between rounded-xl border border-gray-200 p-3" data-testid={`family-account-${f.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center"><Mail className="w-4 h-4" /></div>
                    <div>
                      <p className="font-medium text-gray-800">{f.name}</p>
                      <p className="text-sm text-gray-500">{f.email}</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="rounded-lg text-red-600" onClick={() => removeFamily(f.id)} data-testid={`delete-family-${f.id}`}>
                    <Trash className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function CaregiverDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState(null);
  const [tab, setTab] = useState("patients");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/patients");
      setPatients(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/paypal/subscription").then(({ data }) => {
      if (data.free && !data.paid) {
        toast.info("Votre accès offert concerne l'espace Aidant & Famille. L'espace Soignant & Patient demande un abonnement.");
        navigate("/famille", { replace: true });
        return;
      }
      if (data.trial_expired) {
        toast.info("Votre essai gratuit de 7 jours est terminé. Choisissez une formule pour retrouver l'espace Soignant & Patient.", { id: "trial-expired" });
        navigate("/abonnement", { replace: true });
        return;
      }
      setSub(data);
    }).catch(() => {});
  }, [navigate]);

  const remove = async (id) => {
    try {
      await api.delete(`/patients/${id}`);
      toast.success("Profil supprimé");
      load();
    } catch {
      toast.error("Suppression impossible.");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/connexion");
  };

  return (
    <div className="min-h-screen bg-[#F4F0E8] font-care">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 min-h-16 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 whitespace-nowrap">
            <img src={LOGO} alt="Passerelle" className="h-10 w-auto" data-testid="dashboard-logo" />
            <span className="text-gray-300 hidden sm:inline">·</span>
            <span className="text-gray-500 text-sm hidden sm:inline">Espace soignant</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-600 hidden xl:block whitespace-nowrap">{user?.name}</span>
            {(sub?.active_until || sub?.free || sub?.trial) && (
              <button
                onClick={() => navigate("/abonnement")}
                data-testid="subscription-badge"
                className={`hidden md:inline-flex items-center gap-1.5 text-xs font-semibold px-3 h-7 rounded-full transition-colors ${
                  sub.active
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                    : "bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
                }`}
              >
                {sub.free
                  ? "Accès offert"
                  : sub.trial
                  ? `Essai gratuit · ${trialDaysLeft(sub)} j restants`
                  : sub.active
                  ? `Actif jusqu'au ${new Date(sub.active_until).toLocaleDateString("fr-FR")}`
                  : "Abonnement expiré"}
              </button>
            )}
            <Button variant="outline" size="sm" onClick={() => navigate("/ephemeride")} className="rounded-full" data-testid="ephemeride-link-button">
              <CalendarDays className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Éphéméride</span>
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/abonnement")} className="rounded-full" data-testid="subscription-link-button">
              <CreditCard className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Mon abonnement</span>
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/espaces")} className="rounded-full" data-testid="switch-space-button">
              <LayoutGrid className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Changer d'espace</span>
            </Button>
            {user?.role === "admin" && (
              <Button variant="outline" size="sm" onClick={() => navigate("/admin")} className="rounded-full" data-testid="admin-link-button">
                <Shield className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Administration</span>
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-button">
              <LogOut className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Déconnexion</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <TrialBanner sub={sub} className="mb-6" />
        {selected ? (
          <PatientDetail patient={selected} onBack={() => { setSelected(null); load(); }} />
        ) : (
          <>
            <div className="flex flex-wrap gap-2 mb-8">
              <button onClick={() => setTab("patients")} data-testid="dashboard-tab-patients"
                className={`px-5 h-11 rounded-full font-semibold flex items-center gap-2 ${tab === "patients" ? "bg-emerald-700 text-white" : "bg-white border border-gray-200 text-gray-600"}`}>
                <Users className="w-4 h-4" /> Mes patients
              </button>
              <button onClick={() => setTab("groupes")} data-testid="dashboard-tab-groupes"
                className={`px-5 h-11 rounded-full font-semibold flex items-center gap-2 ${tab === "groupes" ? "bg-emerald-700 text-white" : "bg-white border border-gray-200 text-gray-600"}`}>
                <Users className="w-4 h-4" /> Groupes
              </button>
              <button onClick={() => setTab("gardiens")} data-testid="dashboard-tab-gardiens"
                className={`px-5 h-11 rounded-full font-semibold flex items-center gap-2 ${tab === "gardiens" ? "bg-emerald-700 text-white" : "bg-white border border-gray-200 text-gray-600"}`}>
                <Feather className="w-4 h-4" /> Les Gardiens
              </button>
            </div>

            {tab === "gardiens" ? (
              <GardiensGallery embedded />
            ) : tab === "groupes" ? (
              <GroupsManager patients={patients} />
            ) : (
            <>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-serif-display text-3xl font-normal text-gray-800">Mes patients</h1>
                <p className="text-gray-500 mt-1">Suivez les progrès et lancez les activités.</p>
              </div>
              <AddPatientDialog onCreated={load} />
            </div>
            <GardienBadge id="madeleine" eyebrow="Gardienne du suivi" className="mb-8 max-w-2xl" testId="dashboard-gardien" />

            {loading ? (
              <p className="text-gray-400">Chargement...</p>
            ) : patients.length === 0 ? (
              <Card className="border-dashed border-2 border-gray-300 bg-transparent">
                <CardContent className="p-14 flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
                    <UserPlus className="w-8 h-8 text-emerald-700" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Aucun patient pour l'instant</h3>
                  <p className="text-gray-500 max-w-sm">
                    Créez un premier profil pour commencer les exercices de cognition et de mobilité fine.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="patients-grid">
                {patients.map((p) => (
                  <Card key={p.id} className="border-gray-200 overflow-hidden" data-testid={`patient-card-${p.id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4 mb-5">
                        <img src={p.avatar || AVATARS[0]} alt={p.name} data-testid={`patient-card-portrait-${p.id}`}
                          className="w-16 h-16 rounded-full object-cover object-top border-4 border-amber-200" />
                        <div className="min-w-0">
                          <h3 className="font-bold text-lg text-gray-800 truncate">{p.name}</h3>
                          <p className="text-sm text-gray-500 capitalize">{p.condition || "—"}</p>
                        </div>
                      </div>
                      {p.new_voices > 0 && (
                        <button onClick={() => setSelected(p)} data-testid={`patient-new-voices-${p.id}`}
                          className="mb-3 inline-flex items-center gap-2 rounded-full bg-rose-50 border border-rose-200 text-rose-600 px-4 h-9 text-sm font-semibold hover:bg-rose-100 transition-colors">
                          <span className="w-2 h-2 rounded-full bg-rose-500 soft-pulse" /> Nouveau : {p.new_voices} voix de proche{p.new_voices > 1 ? "s" : ""}
                        </button>
                      )}
                      {p.new_team_messages > 0 && (
                        <button onClick={() => setSelected(p)} data-testid={`patient-team-messages-${p.id}`}
                          className="mb-3 ml-1 inline-flex items-center gap-2 rounded-full bg-sky-50 border border-sky-200 text-sky-700 px-4 h-9 text-sm font-semibold hover:bg-sky-100 transition-colors">
                          <span className="w-2 h-2 rounded-full bg-sky-500 soft-pulse" /> {p.new_team_messages} message{p.new_team_messages > 1 ? "s" : ""} de la famille
                        </button>
                      )}
                      <GardienPickerButton personName={p.name} current={p.gardien_id} testId={`patient-gardien-button-${p.id}`} className="mb-4"
                        onSave={async (gid) => { await api.put(`/patients/${p.id}/gardien`, { gardien_id: gid }); load(); }} />
                      <div className="flex gap-2">
                        <Button onClick={() => navigate(`/patient/${p.id}`)}
                          className="flex-1 bg-emerald-700 hover:bg-emerald-800 rounded-xl"
                          data-testid={`launch-patient-${p.id}`}>
                          <Play className="w-4 h-4 mr-1" /> Activités
                        </Button>
                        <Button variant="outline" onClick={() => setSelected(p)}
                          className="rounded-xl" data-testid={`view-progress-${p.id}`}>
                          <Activity className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" className="rounded-xl text-red-600 hover:text-red-700"
                              data-testid={`delete-patient-${p.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Supprimer ce profil ?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Toutes les sessions de {p.name} seront définitivement supprimées.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuler</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(p.id)} className="bg-red-600 hover:bg-red-700"
                                data-testid={`confirm-delete-${p.id}`}>
                                Supprimer
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
